import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Mic, Square, Loader2, MessageSquare, Phone, ChevronRight, User, PhoneCall, Mail } from "lucide-react";
import { PBX_API_ORIGIN } from "@/lib/apiConfig";

function ActionButton({ action, onNavigate }) {
  if (!action) return null;
  return (
    <Link
      to={action.href}
      onClick={onNavigate}
      className="inline-flex items-center gap-1 bg-[#0047FF] text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-blue-700 transition-colors"
    >
      {action.label} <ChevronRight size={12} />
    </Link>
  );
}

// Stable per-browser identity — survives closing/reopening the widget and
// page reloads, so the visitor is only asked once and the transcript can be
// restored from the server rather than lost with the component's state.
// Each persona (the default OwnPBX assistant, or one of the use-case demo
// agents) gets its own session id, so trying multiple agents doesn't mix
// their conversation histories together.
const SESSION_KEY = "ownpbx_demo_session_id";
const VISITOR_KEY = "ownpbx_demo_visitor";
const sessionIdCache = {};

export function getSessionId(agentId) {
  const key = agentId ? `${SESSION_KEY}_${agentId}` : SESSION_KEY;
  if (sessionIdCache[key]) return sessionIdCache[key];
  try {
    let id = localStorage.getItem(key);
    if (!id) {
      id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(key, id);
    }
    sessionIdCache[key] = id;
    return id;
  } catch {
    const id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionIdCache[key] = id;
    return id;
  }
}

export function getStoredVisitor() {
  try {
    return JSON.parse(localStorage.getItem(VISITOR_KEY) || "null");
  } catch {
    return null;
  }
}

export function storeVisitor(v) {
  try {
    localStorage.setItem(VISITOR_KEY, JSON.stringify(v));
  } catch {}
}

function VisitorGate({ onSubmit }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);

  const canSubmit = name.trim() && phone.trim() && consent;

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    const visitor = { name: name.trim(), phone: phone.trim(), email: email.trim() || null };
    storeVisitor(visitor);
    onSubmit(visitor);
  };

  return (
    <form onSubmit={submit} className="flex flex-col h-full px-4 py-5 overflow-y-auto">
      <p className="text-[13px] text-neutral-300 leading-relaxed mb-4">
        Quick intro before you try the live AI — just your name and number so we know who's testing it.
      </p>
      <div className="space-y-3">
        <div className="relative">
          <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full pl-9 pr-3 py-2.5 bg-white/5 text-white text-[13px] border border-white/10 focus:outline-none focus:border-[#0047FF] transition-colors placeholder:text-neutral-500"
          />
        </div>
        <div className="relative">
          <PhoneCall size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number"
            type="tel"
            className="w-full pl-9 pr-3 py-2.5 bg-white/5 text-white text-[13px] border border-white/10 focus:outline-none focus:border-[#0047FF] transition-colors placeholder:text-neutral-500"
          />
        </div>
        <div className="relative">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            type="email"
            className="w-full pl-9 pr-3 py-2.5 bg-white/5 text-white text-[13px] border border-white/10 focus:outline-none focus:border-[#0047FF] transition-colors placeholder:text-neutral-500"
          />
        </div>
        {touched && (!name.trim() || !phone.trim()) && <p className="text-[11px] text-red-400">Name and phone number are required.</p>}
        {/* Plain, itemized statement of what the demo backend (pbx
            routes/publicdemo.js) actually stores and does — keep in step
            with that file. Requires an affirmative opt-in, not just a
            passive link, per DPDP Act consent requirements. */}
        <label className="flex items-start gap-2.5 pt-1">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 shrink-0 accent-[#0047FF]"
          />
          <span className="text-[11px] leading-relaxed text-neutral-400">
            I agree that my name, number and this conversation (voice included) will be saved so OwnPBX can follow up,
            and processed by AI providers to generate replies, as described in the{" "}
            <Link to="/privacy" target="_blank" className="underline hover:text-white">Privacy Policy</Link>.
          </span>
        </label>
        {touched && name.trim() && phone.trim() && !consent && <p className="text-[11px] text-red-400">Please accept to continue.</p>}
      </div>
      <button
        type="submit"
        className="mt-4 bg-[#0047FF] text-white font-semibold text-[13px] py-2.5 hover:bg-blue-700 transition-colors disabled:opacity-40"
      >
        Start talking to the AI agent
      </button>
    </form>
  );
}

function ChatPanel({ onClose, visitor, agentId, greeting }) {
  const sessionId = getSessionId(agentId);
  const [messages, setMessages] = useState([
    { role: "assistant", content: greeting || "Hi! I'm the OwnPBX AI assistant. Ask me anything about the product — this is the real thing, not a scripted demo." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    fetch(`${PBX_API_ORIGIN}/api/public-demo/session?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => r.json())
      .then((data) => {
        const prior = (data.messages || []).filter((m) => m.channel === "chat");
        if (prior.length) setMessages(prior.map(({ role, content, action }) => ({ role, content, action })));
      })
      .catch(() => {});
  }, [sessionId]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${PBX_API_ORIGIN}/api/public-demo/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.map(({ role, content }) => ({ role, content })),
          session_id: sessionId,
          visitor,
          page_url: window.location.href,
          agent_id: agentId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setMessages((m) => [...m, { role: "assistant", content: data.reply, action: data.action }]);
    } catch (e) {
      setError("Couldn't reach the AI agent right now — please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5">
        {messages.map((m, i) => (
          <div key={i} className={`flex flex-col ${m.role === "assistant" ? "items-start" : "items-end"}`}>
            <div
              className={`text-[13px] leading-snug px-3 py-2 max-w-[85%] ${
                m.role === "assistant" ? "bg-white/8 text-neutral-100" : "bg-[#0047FF] text-white"
              }`}
            >
              {m.content}
            </div>
            {m.action && <div className="mt-1.5"><ActionButton action={m.action} onNavigate={onClose} /></div>}
          </div>
        ))}
        {loading && (
          <div className="bg-white/8 self-start px-3 py-2 inline-flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin text-neutral-400" />
            <span className="text-[11px] text-neutral-400">Thinking…</span>
          </div>
        )}
        {error && <p className="text-[11px] text-red-400">{error}</p>}
      </div>
      <div className="flex items-center gap-2 px-3 py-3 border-t border-white/10">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type your message…"
          className="flex-1 bg-white/5 text-white text-[13px] px-3 py-2.5 border border-white/10 focus:outline-none focus:border-[#0047FF] transition-colors placeholder:text-neutral-500"
        />
        <button
          onClick={send}
          disabled={loading || !input.trim()}
          className="grid place-items-center w-9 h-9 bg-[#0047FF] text-white disabled:opacity-30 shrink-0 hover:bg-blue-700 transition-colors"
          aria-label="Send"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

function CallPanel({ visitor }) {
  const sessionId = getSessionId();
  const [phone, setPhone] = useState(visitor.phone || "");
  const [status, setStatus] = useState("idle"); // idle | requesting | success | error
  const [message, setMessage] = useState("");

  const requestCall = async () => {
    if (!phone.trim() || status === "requesting") return;
    setStatus("requesting");
    setMessage("");
    try {
      const res = await fetch(`${PBX_API_ORIGIN}/api/public-demo/call-me`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          visitor,
          phone,
          page_url: window.location.href,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setStatus("success");
      setMessage("You'll get a call in just a moment — pick up and talk to the AI live.");
    } catch (e) {
      setStatus("error");
      setMessage(e.message || "Couldn't place the call — please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full px-4 py-5">
      <p className="text-[13px] text-neutral-300 leading-relaxed mb-4">
        Skip the typing — get a real phone call from our live AI agent right now, powered by our pre-trained voice agent.
      </p>
      <div className="relative">
        <PhoneCall size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone number"
          type="tel"
          disabled={status === "requesting" || status === "success"}
          className="w-full pl-9 pr-3 py-2.5 bg-white/5 text-white text-[13px] border border-white/10 focus:outline-none focus:border-[#0047FF] transition-colors placeholder:text-neutral-500 disabled:opacity-50"
        />
      </div>
      <button
        onClick={requestCall}
        disabled={!phone.trim() || status === "requesting" || status === "success"}
        className="mt-3 flex items-center justify-center gap-2 bg-[#0047FF] text-white font-semibold text-[13px] py-2.5 hover:bg-blue-700 transition-colors disabled:opacity-40"
      >
        {status === "requesting" ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Calling…
          </>
        ) : (
          <>
            <PhoneCall size={14} /> Call me now
          </>
        )}
      </button>
      {message && (
        <p className={`mt-3 text-[12px] leading-relaxed ${status === "error" ? "text-red-400" : "text-emerald-400"}`}>
          {message}
        </p>
      )}
    </div>
  );
}

function VoicePanel({ onClose, visitor }) {
  const sessionId = getSessionId();
  const [turns, setTurns] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | recording | processing | speaking | error
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const listRef = useRef(null);

  // Voice-activity detection — auto-stops recording after a pause in speech,
  // so a visitor doesn't have to remember to tap stop (real callers don't).
  const audioContextRef = useRef(null);
  const vadRafRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const maxRecordTimerRef = useRef(null);
  const hasSpokenRef = useRef(false);
  const SPEECH_LEVEL = 16; // 0-255 scale from analyser data
  const SILENCE_LEVEL = 10;
  const SILENCE_HANGOVER_MS = 650;
  const MAX_RECORD_MS = 20000;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns, status]);

  useEffect(() => {
    fetch(`${PBX_API_ORIGIN}/api/public-demo/session?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => r.json())
      .then((data) => {
        const prior = (data.messages || []).filter((m) => m.channel === "voice");
        if (prior.length) setTurns(prior.map((m) => ({ who: m.role === "user" ? "You" : "AI", text: m.content, action: m.action })));
      })
      .catch(() => {});
  }, [sessionId]);

  const stopVad = () => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (maxRecordTimerRef.current) clearTimeout(maxRecordTimerRef.current);
    vadRafRef.current = null;
    silenceTimerRef.current = null;
    maxRecordTimerRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  useEffect(() => () => {
    stopVad();
    mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
  }, []);

  const startVad = (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return; // no VAD support — manual stop button still works
    const ctx = new AudioContextClass();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    audioContextRef.current = ctx;
    hasSpokenRef.current = false;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      if (avg > SPEECH_LEVEL) {
        hasSpokenRef.current = true;
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
      } else if (hasSpokenRef.current && avg <= SILENCE_LEVEL && !silenceTimerRef.current) {
        silenceTimerRef.current = setTimeout(() => stopRecording(), SILENCE_HANGOVER_MS);
      }
      vadRafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const startRecording = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stopVad();
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        sendVoice(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setStatus("recording");
      startVad(stream);
      maxRecordTimerRef.current = setTimeout(() => stopRecording(), MAX_RECORD_MS);
    } catch (e) {
      setStatus("error");
      setError("Microphone access denied — allow mic access to try the voice demo.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setStatus("processing");
    }
  };

  const sendVoice = async (blob) => {
    try {
      const res = await fetch(`${PBX_API_ORIGIN}/api/public-demo/voice`, {
        method: "POST",
        headers: {
          "X-Session-Id": sessionId,
          "X-Visitor": encodeURIComponent(JSON.stringify(visitor)),
          "X-Page-Url": encodeURIComponent(window.location.href),
        },
        body: blob,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong");
      }
      const userText = decodeURIComponent(res.headers.get("X-User-Text") || "");
      const aiText = decodeURIComponent(res.headers.get("X-Ai-Text") || "");
      const actionHeader = res.headers.get("X-Action");
      const action = actionHeader ? JSON.parse(decodeURIComponent(actionHeader)) : null;
      const audioBlob = await res.blob();
      setTurns((t) => [...t, { who: "You", text: userText }, { who: "AI", text: aiText, action }]);
      setStatus("speaking");
      const audioEl = new Audio(URL.createObjectURL(audioBlob));
      audioEl.onended = () => setStatus("idle");
      audioEl.onerror = () => setStatus("idle");
      audioEl.play().catch(() => setStatus("idle"));
    } catch (e) {
      setStatus("error");
      setError(e.message || "Couldn't process that — please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 font-mono-t text-[12px]">
        {turns.length === 0 && (
          <p className="text-neutral-500 text-[12px] leading-relaxed">
            Tap the mic and start talking — it replies automatically as soon as you pause, just like a phone call.
          </p>
        )}
        {turns.map((t, i) => (
          <div key={i}>
            <div className="flex gap-2">
              <span className={`shrink-0 uppercase text-[9px] tracking-wider pt-[2px] ${t.who === "AI" ? "text-[#0047FF] font-semibold" : "text-neutral-500"}`}>{t.who}</span>
              <span className="text-neutral-200">{t.text}</span>
            </div>
            {t.action && <div className="mt-1.5 ml-[38px]"><ActionButton action={t.action} onNavigate={onClose} /></div>}
          </div>
        ))}
        {error && <p className="text-[11px] text-red-400">{error}</p>}
      </div>
      <div className="flex flex-col items-center gap-2 px-4 py-5 border-t border-white/10">
        <button
          onClick={status === "recording" ? stopRecording : status === "idle" || status === "error" ? startRecording : undefined}
          disabled={status === "processing" || status === "speaking"}
          className={`grid place-items-center w-14 h-14 rounded-full transition-colors ${
            status === "recording" ? "bg-red-600 text-white" : "bg-[#0047FF] text-white disabled:opacity-40"
          }`}
          aria-label={status === "recording" ? "Stop recording" : "Start recording"}
        >
          {status === "processing" || status === "speaking" ? (
            <Loader2 size={20} className="animate-spin" />
          ) : status === "recording" ? (
            <Square size={18} fill="currentColor" />
          ) : (
            <Mic size={20} />
          )}
        </button>
        <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">
          {status === "recording" ? "Listening… pause when done" : status === "processing" ? "Thinking…" : status === "speaking" ? "Speaking…" : "Tap to talk"}
        </span>
      </div>
    </div>
  );
}

// Any button anywhere on the site can open the widget with
// window.dispatchEvent(new Event("ownpbx:open-demo")) — used by the Hero
// badge's "Try it now" and can be reused by other CTAs without prop-drilling
// through Layout. Pass a persona to talk to one of the use-case demo agents
// instead of the default OwnPBX assistant — {agentId, label, greeting}.
export const openLiveDemo = (persona) => window.dispatchEvent(new CustomEvent("ownpbx:open-demo", { detail: persona || null }));

export const LiveDemoWidget = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat");
  const [visitor, setVisitor] = useState(() => getStoredVisitor());
  const [persona, setPersona] = useState(null);

  useEffect(() => {
    const onOpen = (e) => { setPersona(e.detail || null); setOpen(true); };
    window.addEventListener("ownpbx:open-demo", onOpen);
    return () => window.removeEventListener("ownpbx:open-demo", onOpen);
  }, []);

  return (
    <div className="fixed bottom-5 right-5 z-[60]" data-testid="live-demo-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="mb-3 w-[320px] sm:w-[360px] h-[460px] bg-[#0a0a0a] border border-black shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-3.5 py-3 border-b border-white/10 shrink-0">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-7 h-7 bg-white text-neutral-900 text-[11px] font-bold font-heading">AI</span>
                <div className="leading-tight">
                  <p className="text-[12px] font-semibold text-white">{persona?.label || "OwnPBX Assistant"}</p>
                  <p className="text-[10px] text-neutral-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0047FF] animate-pulse" /> Live · real AI
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white transition-colors" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {visitor ? (
              <>
                {/* Voice and Call tabs temporarily hidden — chat is the only
                    channel proven reliable right now. Re-enable by restoring
                    the tab bar + mode switch removed here. */}
                <div className="flex-1 min-h-0">
                  <ChatPanel
                    key={persona?.agentId || "default"}
                    onClose={() => setOpen(false)}
                    visitor={visitor}
                    agentId={persona?.agentId}
                    greeting={persona?.greeting}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 min-h-0">
                <VisitorGate onSubmit={setVisitor} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative">
        <div className="absolute right-full top-0 h-full flex items-center mr-2.5 pointer-events-none">
          <AnimatePresence>
            {!open && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                className="flex items-center gap-1"
              >
                <span className="whitespace-nowrap bg-white text-neutral-900 text-[12px] font-semibold px-3.5 py-2.5 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.3)] border border-black/10">
                  Test the AI agent live on our site
                </span>
                <ChevronRight size={16} className="text-neutral-900 -ml-1 shrink-0" strokeWidth={3} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <motion.button
          onClick={() => setOpen((o) => !o)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="grid place-items-center w-14 h-14 bg-neutral-900 text-white rounded-full shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] hover:bg-[#0047FF] transition-colors"
          aria-label={open ? "Close live demo" : "Try the AI agent live"}
          data-testid="live-demo-launcher"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={open ? "close" : "open"} initial={{ opacity: 0, rotate: -45 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: 45 }} transition={{ duration: 0.15 }}>
              {open ? <X size={20} /> : <Sparkles size={20} />}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>
    </div>
  );
};
